import React from 'react';
import styled from 'styled-components';

import { GUID, PersonOrg } from '@ndvr/auth-api';

import { useInternalUserDisplayName, useInternalUserProfilesQuery } from '@/modules/internal-user-queries';

const IMPERSONATION_STORAGE_KEY = 'impersonated_user_guid';

export const setImpersonatedUser = (userGUID: GUID<PersonOrg>) => {
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, userGUID);
};

export const getImpersonatedUser = (): GUID<PersonOrg> | null => {
    return localStorage.getItem(IMPERSONATION_STORAGE_KEY);
};

export const clearImpersonatedUser = () => {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
};

export const ImpersonationBanner = () => {
    const impersonatedUserGUID = getImpersonatedUser();
    return impersonatedUserGUID ? <ImpersonatedUserBanner userGUID={impersonatedUserGUID} /> : null;
};

// Only staff can be impersonated, and a staff member's PersonOrg GUID shares the UUID of their InternalUserProfile. The
// warning matters more than the name, so it shows while the name loads, and the GUID stands in for a name that is not
// available.
const ImpersonatedUserBanner = ({ userGUID }: { userGUID: GUID<PersonOrg> }) => {
    const { isPending } = useInternalUserProfilesQuery();
    const userName = useInternalUserDisplayName()(userGUID) || (isPending ? 'another user' : userGUID);

    return (
        <BannerContainer>
            <BannerContent>
                <WarningIcon>⚠️</WarningIcon>
                <BannerText>
                    You are currently logged in as <strong>{userName}</strong>
                </BannerText>
            </BannerContent>
        </BannerContainer>
    );
};

const BannerContainer = styled.div`
    background-color: #ff9800;
    color: #000;
    padding: 12px 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    border-bottom: 2px solid #f57c00;
    z-index: 1000;
`;

const BannerContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const WarningIcon = styled.span`
    font-size: 18px;
`;

const BannerText = styled.span`
    strong {
        font-weight: 700;
    }
`;
